// baseline.ts — cold-start helpers (Milestone D follow-ups):
//   1. ga4Baseline — instant baseline from GA4 history (store-level metrics only)
//   2. cleanupProvisionedPrimitives — archive assistant-provisioned cohorts/events
//      that no active goal references anymore (iteration-close hygiene)

import { funnelService } from '@/api/funnel'
import { goalService, type Indicator } from '@/api/goals'
import { cohortService } from '@/api/cohorts'
import { archiveCustomEvent } from '@/api/customEvents'

const GA4_BACKFILL_WINDOW = 90

const num = (v: any): number => (typeof v === 'number' && isFinite(v) ? v : Number(v) || 0)

/**
 * Try to compute a baseline from GA4 history for a store_funnel metric, so a
 * fresh store doesn't have to wait out the calibration window. Returns null for
 * non-store_funnel bindings, when GA4 isn't connected, or when the value can't be
 * derived — in which case the calibration-window path takes over.
 */
export async function ga4Baseline(
  binding: Indicator['binding'],
  storeId: string
): Promise<Indicator['baseline'] | null> {
  if (!binding || binding.kind !== 'store_funnel') return null
  let ga4: any
  try { ga4 = await funnelService.getGa4StoreFunnelAnalytics(storeId, GA4_BACKFILL_WINDOW) } catch { return null }
  if (!ga4) return null
  const a = ga4.ga4_analytics || ga4 || {}
  const sessions = num(a.sessions ?? a.total_sessions ?? a.session_count)
  const purchases = num(a.purchases ?? a.conversions ?? a.total_conversions ?? a.purchase_count)
  const revenue = num(a.revenue ?? a.total_revenue ?? a.purchase_revenue)

  let value: number | null = null
  switch (binding.metric) {
    case 'conversion_rate': value = sessions > 0 ? purchases / sessions : null; break
    case 'revenue': value = revenue || null; break
    case 'aov': value = purchases > 0 ? revenue / purchases : null; break
    case 'total_funnel_hits': value = sessions || null; break
  }
  if (value == null || !isFinite(value)) return null
  return { value, source: 'ga4_backfill', captured_at: new Date().toISOString() }
}

/** Returns the indicator with a GA4 baseline applied if one can be derived; otherwise unchanged. */
export async function withGa4Baseline(indicator: Indicator, storeId: string): Promise<Indicator> {
  if (indicator.baseline) return indicator
  // Segmented goals calibrate from their SEGMENT cell (backend resolveSegment), not a store-level GA4
  // baseline — a store baseline would mis-seed the segment and block the cell-level lock. Skip it.
  if (indicator.segment) return indicator
  const bl = await ga4Baseline(indicator.binding, storeId)
  return bl ? { ...indicator, baseline: bl, state: 'live' } : indicator
}

/**
 * After a goal is archived, archive the cohorts/events the assistant PROVISIONED
 * for it (recorded in actionable.provisions[]) that no remaining ACTIVE goal
 * references. Only touches assistant-provisioned primitives — never manually
 * created ones — and is best-effort (failures don't block).
 */
export async function cleanupProvisionedPrimitives(
  archivedGoalId: string
): Promise<{ cohortsArchived: number; eventsArchived: number }> {
  const result = { cohortsArchived: 0, eventsArchived: 0 }

  // 1. What did this goal provision?
  const archived = await goalService.getGoal(archivedGoalId).catch(() => null)
  if (!archived) return result
  const provisioned: { kind: string; ref_id: string }[] = []
  for (const act of archived.actionables || []) {
    for (const p of (act.provisions || [])) {
      if (p?.status === 'created' && p?.ref_id) provisioned.push({ kind: p.kind, ref_id: p.ref_id })
    }
  }
  if (!provisioned.length) return result

  // 2. Collect primitive ids still referenced by any ACTIVE goal (listGoals excludes archived).
  const referenced = new Set<string>()
  const collect = (ind?: Indicator) => {
    const b = ind?.binding
    if (b?.cohort_id) referenced.add(b.cohort_id)
    if (b?.event_id) referenced.add(b.event_id)
  }
  try {
    const list = await goalService.listGoals()
    for (const g of list.goals) {
      collect(g.primary_metric)
      const detail = await goalService.getGoal(g.goal_id).catch(() => null)
      for (const act of detail?.actionables || []) (act.indicators || []).forEach(collect)
    }
  } catch { /* if we can't confirm references, don't archive anything */ return result }

  // 3. Archive provisioned primitives nothing active uses anymore.
  for (const p of provisioned) {
    if (referenced.has(p.ref_id)) continue
    try {
      if (p.kind === 'cohort') { await cohortService.archiveCohort(p.ref_id); result.cohortsArchived++ }
      else { await archiveCustomEvent(p.ref_id); result.eventsArchived++ }
    } catch { /* best effort */ }
  }
  return result
}
